"use client";

import { useState } from "react";
import Image from "next/image";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import { Link } from "@/i18n/navigation";

/**
 * Les outils, ailleurs que sur cette page : sur le site d'un autre, et dans
 * l'assistant d'IA du lecteur.
 *
 * EN PIED DE PAGE, APRÈS LA LISTE. Celui qui vient calculer n'a pas à lire
 * ça avant d'avoir trouvé son outil. Il s'adresse à ceux qui écrivent pour des
 * solopreneurs (blogs, cabinets, incubateurs) et à ceux qui posent déjà leurs
 * questions à ChatGPT, à Claude ou à Gemini.
 *
 * DEUX CARTES BÂTIES PAREIL. Une rangée de tuiles en tête, le titre, le
 * texte, puis ce qu'il y a à faire, calé en bas : les deux cartes finissent à
 * la même hauteur quel que soit leur texte. À gauche, un aperçu du code ; à
 * droite, l'adresse à copier.
 *
 * LES TUILES DES ASSISTANTS SONT CELLES DES FORMATIONS
 * (public/images/site/courses/icon-*.png) : mêmes coins, même motif blanc sur
 * aplat. Celle de Gemini a été dessinée le 24/09/2026 aux mêmes proportions.
 *
 * L'ADRESSE DU SERVEUR SE COPIE D'UN CLIC : c'est la seule chose à retenir
 * pour brancher un assistant, et une faute de frappe le laisserait muet.
 */

const ASSISTANTS = [
    { nom: "ChatGPT", image: "/images/site/courses/icon-chatgpt.png" },
    { nom: "Claude", image: "/images/site/courses/icon-claudecode.png" },
    { nom: "Gemini", image: "/images/site/courses/icon-gemini.png" },
];

function Carte({ tuiles, titre, texte, children }) {
    return (
        <div className="flex h-full flex-col rounded-2xl border border-gray-200/70 dark:border-white/10 bg-white/60 dark:bg-white/[0.03] p-5 sm:p-6">
            <div className="flex items-center gap-2">{tuiles}</div>
            <div className="mt-4 text-lg font-bold leading-snug dark:text-white">{titre}</div>
            <p className="mt-2 mb-0 text-[15px] leading-7 text-gray-600 dark:text-lightBlue">{texte}</p>
            {/* Ce qu'il y a à faire, toujours en bas de la carte. */}
            <div className="mt-auto pt-5">{children}</div>
        </div>
    );
}

export default function ToolsElsewhere({ t, origine, adresseMcp, exemple }) {
    const [copie, setCopie] = useState(false);
    const adresse = adresseMcp;

    const copier = async () => {
        try {
            await navigator.clipboard.writeText(adresse);
            setCopie(true);
            setTimeout(() => setCopie(false), 2000);
        } catch {
            setCopie(false);
        }
    };

    return (
        // L'ancre « assistants » : les fiches des outils servis aux assistants
        // y renvoient pour expliquer comment brancher le serveur.
        <section id="assistants" className="mt-16 mb-16 scroll-mt-24">
            <h2 className="text-2xl sm:text-3xl font-bold dark:text-white font-sourceSans">{t.titre}</h2>
            {/* grid-cols-1 sur mobile, et non la colonne implicite : celle-ci
                prenait la largeur de l'aperçu du code, écrit sur une ligne, et
                faisait défiler la page de côté (581 pixels pour un écran de
                375). minmax(0, 1fr) laisse la colonne se réduire, et le
                texte tronqué se coupe comme prévu. */}
            <div className="mt-6 grid grid-cols-1 items-stretch gap-4 sm:grid-cols-2">
                <Carte
                    titre={t.siteTitre}
                    texte={t.siteTexte}
                    tuiles={
                        <span className="flex h-10 w-10 items-center justify-center rounded-[10px] bg-[#D04515] text-white">
                            <SolarIcon name="code-square-linear" className="h-5 w-5" />
                        </span>
                    }
                >
                    {/* Un aperçu, pas le code à copier : celui-là est sur la
                        page de chaque outil, avec la bonne adresse. */}
                    <pre
                        aria-hidden="true"
                        className="m-0 overflow-hidden rounded-lg border border-gray-200/70 dark:border-white/10 bg-gray-50 dark:bg-white/[0.04] px-3 py-2.5 text-xs leading-5 text-gray-500 dark:text-lightBlue/80"
                    >
                        <span className="block truncate">{`<iframe src="${exemple}"></iframe>`}</span>
                        <span className="block truncate">{`<script src="${origine}/static/embed-v1.js"></script>`}</span>
                    </pre>
                    <p className="mt-2 mb-0 text-xs leading-5 text-gray-500 dark:text-lightBlue/70">{t.siteAide}</p>
                </Carte>

                <Carte
                    titre={t.iaTitre}
                    texte={t.iaTexte}
                    tuiles={ASSISTANTS.map((a) => (
                        <Image key={a.nom} src={a.image} alt={a.nom} title={a.nom} width={40} height={40} className="h-10 w-10" />
                    ))}
                >
                    <div className="flex items-center gap-2">
                        {/* Un vrai lien : un moteur ou un assistant qui lit la
                            page y trouve l'adresse du serveur, pas seulement
                            un texte à copier. */}
                        <a
                            href={adresse}
                            target="_blank"
                            rel="noopener"
                            className="min-w-0 flex-1 truncate rounded-lg border border-gray-200/70 dark:border-white/10 bg-gray-50 dark:bg-white/[0.04] px-3 py-2 font-mono text-sm text-gray-800 dark:text-lightBlue hover:!text-primary"
                        >
                            {adresse}
                        </a>
                        <button
                            type="button"
                            onClick={copier}
                            aria-label={copie ? t.copie : t.copier}
                            className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-gray-900 dark:bg-white px-3.5 py-2 text-sm font-semibold text-white dark:text-gray-900 transition-opacity hover:opacity-90"
                        >
                            <SolarIcon name={copie ? "check-circle-linear" : "copy-linear"} className="h-4 w-4" />
                            {copie ? t.copie : t.copier}
                        </button>
                    </div>
                    <p className="mt-2 mb-0 text-xs leading-5 text-gray-500 dark:text-lightBlue/70">{t.iaAide}</p>
                </Carte>
            </div>
            <p className="mt-4 mb-0 text-sm text-gray-500 dark:text-lightBlue/70">
                {t.conditions}
                <Link href="/tools-terms" className="underline underline-offset-2">
                    {t.conditionsLien}
                </Link>
                .
            </p>
        </section>
    );
}
